<template>
  <div class="w-100 p-2 mt-2">
    <page-header>
      <i class="fas fa-list"></i> {{ $t('page.title.question_import') }}
      <template v-slot:right>
        <b-button @click="openModal('create-import')" size="sm" class="ml-1" variant="success">{{ $t('btn.add_new') }}</b-button>
      </template>
    </page-header>
    <page-card>
      <template v-slot:header>
        {{ $t('page.filters') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-3">
              <filter-input
                  :filter-query="filterQuery"
                  :filter-operator="'like'"
                  filter-name="name"
                  :label="$t('filters.label.question_import_name')"
                  :placeholder="$t('filters.placeholder.question_import_name')">
              </filter-input>
            </div>

            <div class="col-3">
              <filter-select
                  :filter-query="filterQuery"
                  :options="optionsForFilterStatus"
                  :multiple="false"
                  options-label="text"
                  options-track-by="text"
                  filter-name="status"
                  :label="$t('filters.label.status')"
                  :placeholder="$t('filters.placeholder.status')">
              </filter-select>
            </div>

            <div class="col-3">
              <filter-select
                  :filter-query="filterQuery"
                  :options="selectOptions.type"
                  :multiple="false"
                  options-label="text"
                  options-track-by="text"
                  filter-name="type"
                  :label="$t('filters.label.type')"
                  :placeholder="$t('filters.placeholder.type')">
              </filter-select>
            </div>

            <div class="col-3">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="date"
                  open-direction="left"
                  :label="$t('filters.label.created_at')">
              </filter-date-range-picker>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary">{{ $t('filters.btn.clear') }}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{ $t('filters.btn.apply') }}</b-button>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('page.list') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
          <div class="table-responsive">
            <b-table striped show-empty
               :empty-text="$t('table.no_results')"
               :busy="isBusy"
               hover
               head-variant="dark"
               outlined
               :sort-by.sync="orderColumn"
               :sort-desc.sync="orderDirection"
               :no-local-sorting="true"
               :items="items"
               :fields="fields">

              <template #cell(id)="data">
                <div>{{ data.item.id | shortId }}</div>
              </template>

              <template #cell(date)="data">
                <div>{{ data.item.date | date }}</div>
              </template>

              <template #cell(type)="data">
                {{ $t(`question_type_${data.item.type}`) }}
              </template>

              <template #cell(status)="data">
                <b-badge v-if="data.item.status === 'CREATED'" variant="primary">
                  {{ $t('import.status.created') }}
                </b-badge>
                <b-badge v-if="data.item.status === 'LOADING'" variant="secondary">
                  {{ $t('import.status.loading') }}
                </b-badge>
                <b-badge v-if="data.item.status === 'LOADED'" variant="info">
                  {{ $t('import.status.loaded') }}
                </b-badge>
                <b-badge v-if="data.item.status === 'PROCESSING'" variant="warning">
                  {{ $t('import.status.processing') }}
                </b-badge>
                <b-badge v-if="data.item.status === 'IMPORTED'" variant="success">
                  {{ $t('import.status.imported') }}
                </b-badge>
                <b-badge v-if="data.item.status === 'UNSUCCESSFUL'" variant="danger">
                  {{ $t('import.status.unsuccessful') }}
                </b-badge>
              </template>

              <template #cell(importedMediaNumber)="data">
                <div>{{ data.item.importedMediaNumber }} / {{ data.item.importedNumber }}</div>
              </template>

              <template #cell(actions)="data">
                <div class="table-action--cell">
                  <input type="file" id="multimedia-files" multiple="multiple" @change="uploadMultimedia(data.item.id, $event)" style="display:none;">
                  <b-button :disabled="data.item.type === 'text' || data.item.status !== 'IMPORTED'" variant="success" size="sm" @click="openUploadFileWindow">
                    {{ $t('btn.add_attachments') }}
                  </b-button>
                  <b-button variant="primary" size="sm" @click="exportCsvFile(data.item.id)">
                    {{ $t('btn.show_report') }}
                  </b-button>
                </div>
              </template>

              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>{{ $t('state.loading') }}</strong>
                </div>
              </template>
            </b-table>
          </div>

          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>

        </div>
      </template>
      <template v-slot:footer>
      </template>
    </page-card>
  </div>
</template>

<script>
import FilterInput from "../../components/grid/filters/FilterInput";
import FilterSelect from "../../components/grid/filters/FilterSelect";
import FilterDateRangePicker from "../../components/grid/filters/FilterDateRangePicker";
import GridMixin from '../../components/grid/GridMixin';
import CreateImportModal from "./CreateImportModal";
import ShowStatusMediaImportModal from "./ShowStatusMediaImportModal";
import axios from "axios";
import alertify from "alertifyjs";
import GridPagination from "../../components/grid/grid-pagination/GridPagination";
import PageHeader from "../../components/page/PageHeader";
import PageCard from "../../components/page/PageCard";

export default {
  mixins: [GridMixin],
  components: {
    PageCard,
    PageHeader,
    FilterSelect,
    FilterInput,
    FilterDateRangePicker,
    GridPagination
  },
  data() {
    return {
      gridGetUrl: '/api/question/import',
      importGetUrl: '/api/question/import',
      showDeleted: false,
      orderColumn: '',
      orderDirection: false,
      file: null,
      importedMediaStatus: 'WAITING',
      importMultimediaId: null,
      importMediaStatusInterval: null,
      fields: [
        {key: 'id'},
        {key: 'name', label: this.$t('table.header.import_name'), sortable: true},
        {key: 'type', label: this.$t('table.header.question_type'),sortable: true, class: 'question-row'},
        {key: 'status', label: this.$t('table.header.import_status'), sortable: true, class: 'answers-row'},
        {key: 'questionNumber', label: this.$t('table.header.number_questions_in_file'), sortable: true},
        {key: 'importedNumber', label: this.$t('table.header.number_imported_questions'), sortable: true},
        {key: 'errorsNumber', label: this.$t('table.header.errors'), class: 'lvl-row', sortable: true},
        {key: 'importedMediaNumber', label: this.$t('table.header.imported_media_files')},
        {key: 'date', label: this.$t('table.header.date_import'), sortable: true},
        {key: 'actions', label: this.$t('table.header.actions')}
      ],
      items: [],
      importStatuses: null,
      selectOptions: {
        type: [
          {value: 'text', text: this.$t('option.text')},
          {value: 'image', text: this.$t('option.image')},
          {value: 'sound', text:this.$t('option.sound')},
          {value: 'video', text: this.$t('option.video')},
        ],
      },
    };
  },
  computed: {
    optionsForFilterStatus: function() {
      let statuses = [];

      if(this.importStatuses) {
        statuses = this.importStatuses.map(status => {
          return {
            value: status,
            text: this.$t(`import.status.${status.toLowerCase()}`)
          }
        });
      }

      return statuses;
    }
  },
  mounted() {
    this.getStatuses();
    this.getData(this.importGetUrl+'?limit=1000', ['selectOptions', 'category']);
  },
  methods: {
    getData: function (url, localStorage) {
      axios.get(url)
        .then((response) => {
          if (localStorage[1] === 'category') {
            this[localStorage[0]][localStorage[1]] = response.data.items.map(c => {
              c.translated = this.$t('category.'+c.name);
              return c;
            });
          } else {
            this[localStorage[0]][localStorage[1]] = response.data.items;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    getStatuses() {
      axios.get('/api/question/actual-import-statuses')
          .then(response => {
            this.importStatuses = response.data;
          })
          .catch(error => {
            console.log(error);
          });
    },
    createFilterQuery() {
      let filter = '';
      Object.keys(this.filterQuery).forEach(key => {
        filter += this.filterQuery[key];
      })
      return filter;
    },
    createSortQuery() {
      let sorting;
      if (this.orderColumn) {
        if (this.orderDirection === true) {
          sorting = encodeURIComponent('+' + this.orderColumn);
        } else {
          sorting = encodeURIComponent('-' + this.orderColumn);
        }
        if (sorting.includes('categories')) {
          sorting = sorting.replace('categories', 'categoryName');
        }
        return '&sort[]=' + sorting;
      }
      return '';
    },
    openModal(type) {
      switch (type) {
        case('create-import'): {
          this.$store.commit('modal/openModal', {
            parent: this,
            modal: CreateImportModal,
            modalName: 'createImportModal',
            props: {apiKey: this.apiKey, selectOptions: this.selectOptions}
          });
          break;
        }
        case('show-status-media-import'): {
          this.$store.commit('modal/openModal', {
            parent: this,
            modal: ShowStatusMediaImportModal,
            modalName: 'ShowStatusMediaImportModal',
            props: {
              apiKey: this.apiKey,
              importMultimediaId: this.importMultimediaId
            }
          });
          break;
        }
      }
    },
    openUploadFileWindow() {
      document.getElementById('multimedia-files').click();
    },
    uploadMultimedia(id, event) {
      this.importMultimediaId = id;

      let formData = new FormData();
      const files = event.target.files;

      for (let i = 0; i < files.length; ++i) {
        formData.append('file[]', files[i]);
      }

      axios.post( `/api/question/import/${id}/multimedia`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      ).then(() => {
        this.resetFileInput();
        this.openModal('show-status-media-import');
      }).catch(() => {
        alertify.error('Something went wrong');
      });
    },
    exportCsvFile(id) {
      axios.get(`/api/question/${id}/export`)
        .then((response) => {
          const hiddenElement = document.createElement('a');
          hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(response.data);
          hiddenElement.target = '_blank';
          hiddenElement.download = `Question-import-${id}.csv`;
          hiddenElement.click();
        })
        .catch((error) => {
          alertify.error('Something went wrong');
          console.log(error);
        });
    },
    resetFileInput() {
      document.getElementById("multimedia-files").value = "";
    }
  }
}
</script>
